import React, { useState } from 'react'
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Link,
  TextField,
  Typography
} from '@mui/material'
import theme from '../../theme'

export interface MfaVerificationProps {
  onSubmit: (params: { mfaCode: string; rememberMe: boolean }) => Promise<void>
  onBackToLogin: () => void
  isLoading?: boolean
  error?: string | null
  rememberDefault?: boolean
}

const MfaVerification = ({
  onSubmit,
  onBackToLogin,
  isLoading = false,
  error = null,
  rememberDefault = false
}: MfaVerificationProps) => {
  const [mfaCode, setMfaCode] = useState('')
  const [rememberMe, setRememberMe] = useState(rememberDefault)

  const handleChange = (value: string) => {
    const sanitized = value.replace(/\D/g, '').slice(0, 6)
    setMfaCode(sanitized)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (mfaCode.length === 6 && !isLoading) {
      await onSubmit({ mfaCode, rememberMe })
    }
  }

  return (
    <Box display="flex" flexDirection="column" gap={theme.space['32']}>
      <Box display="flex" flexDirection="row" gap={theme.space['20']} alignItems="center">
        <img
          src="/images/sign-in-icon.svg"
          alt="GTV logo"
          style={{ width: '34px', height: '34px' }}
        />
        <Typography variant="heading-8">2-Step Verification</Typography>
      </Box>

      <Typography variant="body-9" sx={{ color: theme.palette['sage-900'] }}>
        Your account is protected with a verification code. Please enter your 6-digit code to continue to your dashboard.
      </Typography>

      <form onSubmit={handleSubmit}>
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="flex-start"
          gap={theme.space['24']}
          padding={theme.space['40']}
          borderRadius={theme.borders['rounded-10']}
          sx={{ backgroundColor: theme.palette['base-white'] }}
        >
          <TextField
            value={mfaCode}
            onChange={(e) => handleChange(e.target.value)}
            placeholder="Enter 6-digit code"
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 6 }}
            disabled={isLoading}
            sx={{
              width: '100%',
              '& input': { boxSizing: 'border-box', height: '56px', letterSpacing: '6px', textAlign: 'center' }
            }}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                disabled={isLoading}
              />
            }
            label={<Typography variant="body-10">Remember me on this computer</Typography>}
          />

          {error && (
            <Typography color="error" variant="body-10">
              {error}
            </Typography>
          )}
        </Box>

        <Button
          variant="contained"
          type="submit"
          disabled={mfaCode.length !== 6 || isLoading}
          sx={{ height: '56px', width: '100%', boxShadow: 'none', marginTop: theme.space['48'] }}
        >
          <Typography variant="ui-3">Sign In</Typography>
        </Button>
      </form>

      <Box display="flex" justifyContent="center">
        <Link
          component="button"
          type="button"
          onClick={onBackToLogin}
          disabled={isLoading}
          underline="hover"
          sx={{ fontSize: '14px' }}
        >
          Back to Login
        </Link>
      </Box>
    </Box>
  )
}

export default MfaVerification
